import {InlineOrderGuidDto, OrderGuidDto, OrdersGroupByDayGuidDto, OrderStageGuid, OrderWasteTypeGuid, SaveOrderGuidRequest, SubOrderGuidDto, SubOrderGuidRequest, UploadedImageGuidDto} from '@mybake/ui/models/Order/IOrderGuid';
import {IOrder} from '../models/Order/IOrder';
import {DTO_STATE, IS_DELETED, ORDER_STAGES} from '@mybake/ui/common/dictionary';
import {ICustomer} from '@mybake/ui/models/Customers/ICustomer';

const orderStages: Record<number, OrderStageGuid> = {
    0: 'NEW',
    1: 'PREPAID',
    2: 'CANCELED',
    3: 'DONE',
    4: 'PICKED_UP',
};

export const normalizeOrderStageGuid = (stage: OrderStageGuid | number | string): OrderStageGuid => {
    const normalized = typeof stage === 'number' || /^\d+$/.test(stage)
        ? orderStages[Number(stage)]
        : stage.toUpperCase() as OrderStageGuid;
    if (!normalized || !Object.values(orderStages).includes(normalized)) {
        throw new Error(`Unsupported order stage: ${String(stage)}`);
    }
    return normalized;
};

export const normalizeInlineOrderGuid = (order: InlineOrderGuidDto): InlineOrderGuidDto => ({
    ...order,
    stage: normalizeOrderStageGuid(order.stage),
});

export const normalizeOrderGroupsGuid = (groups: OrdersGroupByDayGuidDto[]): OrdersGroupByDayGuidDto[] => groups.map(group => ({
    ...group,
    orders: group.orders.map(normalizeInlineOrderGuid),
}));

export const normalizeOrderGuid = (order: OrderGuidDto): OrderGuidDto => ({
    ...order,
    stage: normalizeOrderStageGuid(order.stage),
});

export const toOrderWasteGuidQueryParams = (simulate: boolean, allowNegativeStock?: boolean) => {
    const params: {simulate?: true; allowNegativeStock?: true} = {};
    if (simulate) params.simulate = true;
    if (allowNegativeStock) params.allowNegativeStock = true;
    return Object.keys(params).length ? params : undefined;
};

export const getMissingOrderSelectionsGuid = (subOrder: SubOrderGuidDto) => ({
    filling: subOrder.hasAvailableFillings && !subOrder.fillingId,
    packages: subOrder.packages.length === 0,
    decorations: subOrder.decorations.length === 0,
});

export const getAvailableOrderWasteTypesGuid = (subOrder?: SubOrderGuidDto): OrderWasteTypeGuid[] => subOrder ? [
    ...(subOrder.recipeId ? ['RECIPE_PART' as const] : []),
    ...(subOrder.fillingId ? ['FILLING' as const] : []),
    ...(subOrder.decorations.length ? ['DECORATION' as const] : []),
    ...(subOrder.packages.length ? ['PACKAGE' as const] : []),
] : [];

export const toSubOrderGuidRequest = (subOrder: SubOrderGuidDto): SubOrderGuidRequest => ({
    id: subOrder.id,
    orderId: subOrder.orderId,
    recipeId: subOrder.recipeId,
    fillingId: subOrder.fillingId,
    description: subOrder.description,
    price: subOrder.price,
    ingredientCost: subOrder.ingredientCost,
    packageCost: subOrder.packageCost,
    productCost: subOrder.productCost,
    count: subOrder.count,
    recipeCounter: subOrder.recipeCounter,
    packages: subOrder.packages.map(({packageId, quantity}) => ({packageId, quantity})),
    decorations: subOrder.decorations.map(({decorationId, quantity}) => ({decorationId, quantity})),
    images: subOrder.images.map(({id, imageId, isResult}) => ({id, imageId, isResult})),
});

export const toUpdateOrderGuidRequest = (order: OrderGuidDto): SaveOrderGuidRequest => ({
    id: order.id,
    description: order.description ?? '',
    customerId: order.customerId,
    pickUpAt: order.pickUpAt,
    subOrders: order.subOrders.map(toSubOrderGuidRequest),
    prepayment: order.prepayment ? {
        id: order.prepayment.id,
        orderId: order.prepayment.orderId,
        prepayment: order.prepayment.prepayment,
        prepaymentAt: order.prepayment.prepaymentAt,
    } : null,
});

const active = <T extends {state?: unknown}>(values: T[] | undefined) => (values ?? []).filter(value => value.state !== DTO_STATE.DTO_DELETED);

export const toSaveOrderGuidRequest = (order: IOrder, uploaded: UploadedImageGuidDto[] = []): SaveOrderGuidRequest => {
    const uploadedByName = new Map(uploaded.map(image => [image.fileName, image.id]));
    const existingOrderId = typeof order.id === 'string' && order.state !== DTO_STATE.DTO_NEW ? order.id : undefined;
    return {
        ...(existingOrderId ? {id: existingOrderId} : {}),
        description: order.description,
        customerId: String(order.customerId),
        pickUpAt: order.pickUpAt ?? '',
        subOrders: active(order.subOrders).map(subOrder => {
            const existingId = typeof subOrder.id === 'string' && subOrder.state !== DTO_STATE.DTO_NEW ? subOrder.id : undefined;
            return {
                ...(existingId ? {id: existingId, orderId: existingOrderId} : {}),
                recipeId: subOrder.recipeId ? String(subOrder.recipeId) : null,
                fillingId: subOrder.fillingId ? String(subOrder.fillingId) : null,
                description: subOrder.description,
                price: Number(subOrder.price), ingredientCost: Number(subOrder.ingredientCost ?? 0), packageCost: Number(subOrder.packageCost ?? 0), productCost: Number(subOrder.productCost),
                count: Number(subOrder.count), recipeCounter: Number(subOrder.recipeCounter),
                packages: active(subOrder.packages).map(value => ({packageId: String(value.packageId), quantity: Number(value.quantity)})),
                decorations: active(subOrder.decorations).map(value => ({decorationId: value.decorationId, quantity: Number(value.quantity)})),
                images: active(subOrder.images).flatMap(image => {
                    const imageId = image.imageId ?? uploadedByName.get(image.fileName);
                    return imageId ? [{...(image.imageId && image.state !== DTO_STATE.DTO_NEW ? {id: String(image.id)} : {}), imageId, isResult: image.isResult ?? image.isResultImage}] : [];
                }),
            };
        }),
        prepayment: order.prepayment && order.prepayment.prepayment > 0 && order.prepayment.prepaymentAt ? {
            ...(typeof order.prepayment.id === 'string' ? {id: order.prepayment.id, orderId: existingOrderId} : {}),
            prepayment: Number(order.prepayment.prepayment),
            prepaymentAt: order.prepayment.prepaymentAt,
        } : null,
    };
};

const stageMap: Record<OrderGuidDto['stage'], ORDER_STAGES> = {NEW: ORDER_STAGES.NEW, PREPAID: ORDER_STAGES.PREPAID, CANCELED: ORDER_STAGES.CANCELED, DONE: ORDER_STAGES.DONE, PICKED_UP: ORDER_STAGES.PICKED_UP};

export const toOrderFormModel = (order: OrderGuidDto, customer?: ICustomer): IOrder => ({
    id: order.id, orderNumber: order.orderNumber, description: order.description ?? '', isDeleted: order.isDeleted, state: DTO_STATE.DTO_UNCHANGED,
    price: order.price, cost: order.cost, wasteCost: order.wasteCost, ingredientCost: order.ingredientCost, packageCost: order.packageCost,
    customerId: order.customerId, customer, stage: stageMap[order.stage], pickUpAt: order.pickUpAt, remainingToPay: String(order.remainingToPay),
    isReceiptIssued: order.isReceiptIssued, canceledByCustomer: order.canceledByCustomer, hasReview: order.hasReview, createdAt: new Date(order.createdAt),
    prepayment: order.prepayment ? {...order.prepayment, state: DTO_STATE.DTO_UNCHANGED, createdAt: order.prepayment.createdAt ? new Date(order.prepayment.createdAt) : undefined} : undefined,
    subOrders: order.subOrders.map(subOrder => ({
        id: subOrder.id, orderId: subOrder.orderId, recipeId: subOrder.recipeId ?? '', recipeName: subOrder.recipeName, fillingId: subOrder.fillingId ?? undefined, fillingName: subOrder.fillingName ?? undefined,
        description: subOrder.description, price: subOrder.price, ingredientCost: subOrder.ingredientCost, packageCost: subOrder.packageCost, productCost: subOrder.productCost, wasteCost: subOrder.wasteCost,
        count: subOrder.count, recipeCounter: subOrder.recipeCounter, isDeleted: subOrder.isDeleted, state: DTO_STATE.DTO_UNCHANGED, hasAvailableFillings: subOrder.hasAvailableFillings,
        packages: subOrder.packages.map(value => ({id: value.packageId, ...value, name: value.name ?? '', state: DTO_STATE.DTO_UNCHANGED})),
        decorations: subOrder.decorations.map(value => ({id: value.decorationId, ...value, name: value.name ?? '', state: DTO_STATE.DTO_UNCHANGED})),
        images: subOrder.images.map(value => ({id: value.id, imageId: value.imageId, subOrderId: subOrder.id, orderId: order.id, fileName: value.fileName, isResultImage: value.isResult, isResult: value.isResult, state: DTO_STATE.DTO_UNCHANGED, createdAt: new Date(value.createdAt)})),
    })),
});
